import { resumeTemplateService } from './resumeTemplateService';

interface ResumeContent {
  personal?: {
    fullName: string;
    email: string;
    phone: string;
    location: string;
  };
  summary?: string;
  experience?: Array<{
    company: string;
    title: string;
    startDate: string;
    endDate?: string;
    description?: string;
    bullets?: string[];
  }>;
  education?: Array<{
    school: string;
    degree: string;
    field: string;
    graduationDate: string;
  }>;
  skills?: any;
  certifications?: string[];
}

class ResumeExportService {
  private formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }

  private getAllSkills(skills: any): string[] {
    if (Array.isArray(skills)) {
      return skills;
    }
    return [
      ...(skills?.technical || []),
      ...(skills?.soft || []),
      ...(skills?.other || []),
    ];
  }

  async exportAsTXT(resumeId: string, content: ResumeContent, userId: string): Promise<string> {
    let text = '';

    text += `${content.personal?.fullName || 'Your Name'}\n`;
    text += `${content.personal?.email || ''} | ${content.personal?.phone || ''}\n`;
    text += `${content.personal?.location || ''}\n\n`;

    if (content.summary) {
      text += `PROFESSIONAL SUMMARY\n`;
      text += `${content.summary}\n\n`;
    }

    if (content.experience && content.experience.length > 0) {
      text += `EXPERIENCE\n\n`;
      content.experience.forEach(exp => {
        text += `${exp.title} at ${exp.company}\n`;
        text += `${this.formatDate(exp.startDate)} - ${exp.endDate ? this.formatDate(exp.endDate) : 'Present'}\n`;
        if (exp.bullets) {
          exp.bullets.forEach(bullet => {
            text += `• ${bullet}\n`;
          });
        }
        text += '\n';
      });
    }

    if (content.education && content.education.length > 0) {
      text += `EDUCATION\n\n`;
      content.education.forEach(edu => {
        text += `${edu.degree} in ${edu.field}\n`;
        text += `${edu.school}, ${this.formatDate(edu.graduationDate)}\n\n`;
      });
    }

    const allSkills = this.getAllSkills(content.skills);
    if (allSkills.length > 0) {
      text += `SKILLS\n`;
      text += allSkills.filter(s => s.trim()).join(', ') + '\n\n';
    }

    if (content.certifications && content.certifications.length > 0) {
      text += `CERTIFICATIONS\n`;
      content.certifications.forEach(cert => {
        text += `• ${cert}\n`;
      });
    }

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    await resumeTemplateService.createResumeExport(
      userId,
      resumeId,
      'txt',
      url,
      blob.size
    );

    return url;
  }

  async exportAsPDF(
    resumeId: string,
    content: ResumeContent,
    templateData: any,
    colorScheme: any,
    userId: string
  ): Promise<string> {
    return new Promise((resolve) => {
      const element = document.createElement('div');
      element.style.width = '816px';
      element.style.height = '1056px';
      element.style.padding = '48px';
      element.style.fontFamily = templateData.fonts?.body || 'Arial';
      element.style.fontSize = '11px';
      element.style.lineHeight = '1.6';
      element.style.color = colorScheme?.text || '#1f2937';
      element.style.backgroundColor = '#ffffff';

      let html = '';

      html += `<div style="text-align: center; margin-bottom: 24px;">`;
      html += `<h1 style="font-size: 28px; font-weight: bold; margin-bottom: 8px; color: ${colorScheme?.primary || '#2563eb'}; font-family: ${templateData.fonts?.heading || 'Arial'};">${content.personal?.fullName || 'Your Name'}</h1>`;
      html += `<div style="font-size: 10px;">${content.personal?.location || ''} | ${content.personal?.phone || ''} | ${content.personal?.email || ''}</div>`;
      html += `</div>`;

      if (content.summary) {
        html += `<div style="margin-bottom: 20px;">`;
        html += `<h2 style="font-size: 14px; font-weight: bold; color: ${colorScheme?.primary || '#2563eb'}; border-bottom: 2px solid ${colorScheme?.primary || '#2563eb'}; padding-bottom: 4px; margin-bottom: 8px;">PROFESSIONAL SUMMARY</h2>`;
        html += `<p>${content.summary}</p>`;
        html += `</div>`;
      }

      if (content.experience && content.experience.length > 0) {
        html += `<div style="margin-bottom: 20px;">`;
        html += `<h2 style="font-size: 14px; font-weight: bold; color: ${colorScheme?.primary || '#2563eb'}; border-bottom: 2px solid ${colorScheme?.primary || '#2563eb'}; padding-bottom: 4px; margin-bottom: 12px;">EXPERIENCE</h2>`;
        content.experience.forEach(exp => {
          html += `<div style="margin-bottom: 16px;">`;
          html += `<div style="display: flex; justify-content: space-between; margin-bottom: 4px;">`;
          html += `<div><strong>${exp.title}</strong> at ${exp.company}</div>`;
          html += `<div style="font-size: 10px;">${this.formatDate(exp.startDate)} - ${exp.endDate ? this.formatDate(exp.endDate) : 'Present'}</div>`;
          html += `</div>`;
          if (exp.bullets && exp.bullets.length > 0) {
            html += `<ul style="margin-left: 20px; font-size: 10px;">`;
            exp.bullets.filter(b => b.trim()).forEach(bullet => {
              html += `<li style="margin-bottom: 4px;">${bullet}</li>`;
            });
            html += `</ul>`;
          }
          html += `</div>`;
        });
        html += `</div>`;
      }

      if (content.education && content.education.length > 0) {
        html += `<div style="margin-bottom: 20px;">`;
        html += `<h2 style="font-size: 14px; font-weight: bold; color: ${colorScheme?.primary || '#2563eb'}; border-bottom: 2px solid ${colorScheme?.primary || '#2563eb'}; padding-bottom: 4px; margin-bottom: 12px;">EDUCATION</h2>`;
        content.education.forEach(edu => {
          html += `<div style="margin-bottom: 8px;">`;
          html += `<div style="font-weight: bold;">${edu.degree} in ${edu.field}</div>`;
          html += `<div style="font-size: 10px;">${edu.school}, ${this.formatDate(edu.graduationDate)}</div>`;
          html += `</div>`;
        });
        html += `</div>`;
      }

      const allSkills = this.getAllSkills(content.skills);
      if (allSkills.length > 0) {
        html += `<div style="margin-bottom: 20px;">`;
        html += `<h2 style="font-size: 14px; font-weight: bold; color: ${colorScheme?.primary || '#2563eb'}; border-bottom: 2px solid ${colorScheme?.primary || '#2563eb'}; padding-bottom: 4px; margin-bottom: 12px;">SKILLS</h2>`;
        html += `<div style="font-size: 10px;">${allSkills.filter(s => s.trim()).join(' • ')}</div>`;
        html += `</div>`;
      }

      if (content.certifications && content.certifications.length > 0) {
        html += `<div>`;
        html += `<h2 style="font-size: 14px; font-weight: bold; color: ${colorScheme?.primary || '#2563eb'}; border-bottom: 2px solid ${colorScheme?.primary || '#2563eb'}; padding-bottom: 4px; margin-bottom: 12px;">CERTIFICATIONS</h2>`;
        html += `<ul style="margin-left: 20px; font-size: 10px;">`;
        content.certifications.filter(c => c.trim()).forEach(cert => {
          html += `<li>${cert}</li>`;
        });
        html += `</ul>`;
        html += `</div>`;
      }

      element.innerHTML = html;

      const textBlob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(textBlob);

      resumeTemplateService.createResumeExport(
        userId,
        resumeId,
        'pdf',
        url,
        textBlob.size
      );

      resolve(url);
    });
  }

  async exportAsDOCX(resumeId: string, content: ResumeContent, userId: string): Promise<string> {
    let docContent = '';

    docContent += `${content.personal?.fullName || 'Your Name'}\n`;
    docContent += `${content.personal?.email || ''} | ${content.personal?.phone || ''}\n`;
    docContent += `${content.personal?.location || ''}\n\n`;

    if (content.summary) {
      docContent += `PROFESSIONAL SUMMARY\n${content.summary}\n\n`;
    }

    if (content.experience && content.experience.length > 0) {
      docContent += `EXPERIENCE\n\n`;
      content.experience.forEach(exp => {
        docContent += `${exp.title} at ${exp.company}\n`;
        docContent += `${this.formatDate(exp.startDate)} - ${exp.endDate ? this.formatDate(exp.endDate) : 'Present'}\n`;
        if (exp.bullets) {
          exp.bullets.forEach(bullet => {
            docContent += `• ${bullet}\n`;
          });
        }
        docContent += '\n';
      });
    }

    if (content.education && content.education.length > 0) {
      docContent += `EDUCATION\n\n`;
      content.education.forEach(edu => {
        docContent += `${edu.degree} in ${edu.field}\n`;
        docContent += `${edu.school}, ${this.formatDate(edu.graduationDate)}\n\n`;
      });
    }

    const allSkills = this.getAllSkills(content.skills);
    if (allSkills.length > 0) {
      docContent += `SKILLS\n${allSkills.filter(s => s.trim()).join(', ')}\n\n`;
    }

    if (content.certifications && content.certifications.length > 0) {
      docContent += `CERTIFICATIONS\n`;
      content.certifications.forEach(cert => {
        docContent += `• ${cert}\n`;
      });
    }

    const blob = new Blob([docContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);

    await resumeTemplateService.createResumeExport(
      userId,
      resumeId,
      'docx',
      url,
      blob.size
    );

    return url;
  }

  downloadFile(url: string, filename: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export const resumeExportService = new ResumeExportService();
